import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronRight, Search } from 'lucide-react';
import Menu from '../../components/menu';
import DashboardShell from '../../../components/DashboardShell';
import { useMenuState } from '../../../Common/MenuStateContext';
import SaListPageShell from '../../../components/sa/SaListPageShell';
import ContentLoader from '../../../components/ContentLoader';
import RecordDetailModal, {
  type RecordDetailGroup,
} from '../../../components/RecordDetailModal';
import PdfReportPreviewModal from '../../../components/PdfReportPreviewModal';
import TableSortSelect from '../../../components/TableSortSelect';
import { Alert, Button, Tabs } from '../../../components/ui';
import { cn } from '../../../utils/cn';
import { getCompanyList } from '../../../services/companyApi';
import { getProjectList } from '../../../services/projectApi';
import { TABLE_SORT, sortTableRows } from '../../../utils/tableSort';
import {
  useProjectOversightRecords,
  type ProjectOversightRecord,
} from '../../../hooks/useProjectOversightRecords';
import {
  fetchProjectOversightAssessmentDetail,
  type ProjectOversightAssessmentLine,
} from '../../../services/projectOversightApi';
import Chat from '../../components/smechat';
import '../../../assets/css/chat.css';
import '../../../assets/css/chat-theme.css';
import '../../../assets/css/sme-assessment-tracking.css';
import '../../../styles/sme-chat.css';
import '../../../styles/sme-workspace.css';
import { downloadReport } from '../../../services/reportsApi';
import { dashboardToast } from '../../../utils/dashboardToast';
import {
  PROJECT_OVERSIGHT_CONFIG,
  type ProjectOversightMode,
} from './projectOversightConfig';
import '../../../assets/css/dashboard.css';
import '../../../assets/css/record-detail-modal.css';

type CompanyRow = {
  id: number;
  companyName: string;
  industry?: string;
  companyInfo?: string;
};

type ProjectRow = Record<string, unknown> & {
  id: number;
  projectName: string;
  departmentName?: string;
  projectNumber?: string;
  projectCreationDate?: string;
};

const resolveCompanyId = (company: Record<string, unknown>) =>
  Number(company.id ?? company.companyId ?? company.Id);

const normalizeCompany = (raw: Record<string, unknown>): CompanyRow | null => {
  const id = resolveCompanyId(raw);
  if (!Number.isFinite(id) || id <= 0) return null;

  return {
    id,
    companyName: String(raw.companyName ?? raw.CompanyName ?? raw.name ?? `Company ${id}`),
    industry: String(raw.industry ?? raw.Industry ?? '').trim() || undefined,
    companyInfo: String(raw.companyInfo ?? raw.CompanyInfo ?? '').trim() || undefined,
  };
};

const normalizeProject = (raw: Record<string, unknown>): ProjectRow | null => {
  const id = Number(raw.id ?? raw.projectId ?? raw.ProjectId);
  if (!Number.isFinite(id) || id <= 0) return null;

  return {
    ...raw,
    id,
    projectName: String(raw.projectName ?? raw.ProjectName ?? `Project ${id}`),
    departmentName: String(raw.departmentName ?? raw.DepartmentName ?? '').trim() || undefined,
    projectNumber: String(raw.projectNumber ?? raw.ProjectNumber ?? '').trim() || undefined,
    projectCreationDate: raw.projectCreationDate
      ? String(raw.projectCreationDate)
      : raw.creationDate
        ? String(raw.creationDate)
        : undefined,
  };
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

/** Full timestamp for report tables (avoids clipped locale strings in narrow columns). */
const formatReportTimestamp = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  };
  const datePart = new Intl.DateTimeFormat('en-US', options).format(date);
  const timePart = date.toLocaleTimeString('en-US');
  return `${datePart}, ${timePart}`;
};

const normalizeReportStatus = (value?: string) =>
  String(value || 'submitted').trim().toLowerCase();

const isSavedReportStatus = (value?: string) => {
  const status = normalizeReportStatus(value);
  return status === 'edit' || status === 'upload';
};

const reportStatusLabel = (value?: string) => {
  const status = normalizeReportStatus(value);
  if (status === 'edit') return 'Edit';
  if (status === 'upload') return 'Upload';
  return 'Submitted';
};

const reportStatusClass = (value?: string) => {
  const status = normalizeReportStatus(value);
  if (status === 'submitted') return 'app-status app-status--success';
  if (status === 'edit') return 'app-status app-status--info';
  if (status === 'upload') return 'app-status app-status--warning';
  return 'app-status app-status--muted';
};

/** Client-aligned assessment progress (Not started | In progress | Completed). */
const normalizeAssessmentProgressStatus = (status?: string) => {
  const raw = (status || '').trim();
  if (!raw) return 'Not started';
  const head = raw.split('·')[0].trim().toLowerCase();
  if (head.includes('complete')) return 'Completed';
  if (head.includes('progress')) return 'In progress';
  if (head.includes('not started')) return 'Not started';
  return raw.split('·')[0].trim() || raw;
};

const statusClass = (status?: string) => {
  const label = normalizeAssessmentProgressStatus(status).toLowerCase();
  if (label === 'completed') {
    return 'app-status app-status--success';
  }
  if (label === 'in progress') {
    return 'app-status app-status--info';
  }
  return 'app-status app-status--muted';
};

const emptyAnswer = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
};

const renderAssessmentResponses = (lines: ProjectOversightAssessmentLine[]) => {
  if (lines.length === 0) {
    return <p className="sa-oversight-assessment-responses__empty">No assessment answers recorded yet.</p>;
  }

  return (
    <div className="sa-oversight-assessment-responses">
      {lines.map((line, index) => (
        <article key={`${line.capabilityName}-${line.subCapabilityName}-${index}`} className="sa-oversight-assessment-response">
          <h6 className="sa-oversight-assessment-response__title">
            {line.capabilityName}
            {line.subCapabilityName ? ` · ${line.subCapabilityName}` : ''}
          </h6>
          <p className="sa-oversight-assessment-response__ratings">
            <span className="sa-oversight-assessment-response__label">Ratings</span>
            Current {line.currentRating ?? '—'} · Future {line.futureRating ?? '—'}
          </p>
          <div className="sa-oversight-assessment-response__answer">
            <span className="sa-oversight-assessment-response__label">Current description</span>
            <p>{emptyAnswer(line.currentDescription)}</p>
          </div>
          <div className="sa-oversight-assessment-response__answer">
            <span className="sa-oversight-assessment-response__label">Future description</span>
            <p>{emptyAnswer(line.futureDescription)}</p>
          </div>
        </article>
      ))}
    </div>
  );
};

type ProjectOversightLayoutProps = {
  mode: ProjectOversightMode;
};

export function ProjectOversightLayout({ mode }: ProjectOversightLayoutProps) {
  const config = PROJECT_OVERSIGHT_CONFIG[mode];
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [selectedItem, setSelectedItem] = useState(null);

  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [companiesError, setCompaniesError] = useState<string | null>(null);

  const [visibleSection, setVisibleSection] = useState<number | null>(null);
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const [projectsByCompany, setProjectsByCompany] = useState<Record<number, ProjectRow[]>>({});
  const [loadingProjects, setLoadingProjects] = useState<Record<number, boolean>>({});

  const [activeProject, setActiveProject] = useState<ProjectRow | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState(TABLE_SORT.NAME_ASC);

  const [detailRecord, setDetailRecord] = useState<ProjectOversightRecord | null>(null);
  const [detailGroups, setDetailGroups] = useState<RecordDetailGroup[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [previewReport, setPreviewReport] = useState<ProjectOversightRecord | null>(null);
  const [reportHistoryTab, setReportHistoryTab] = useState<'saved' | 'submitted'>('saved');

  const activeProjectId = activeProject?.id ?? null;
  const { records, loading: loadingRecords, error: recordsError } = useProjectOversightRecords(
    mode,
    activeProjectId
  );

  useEffect(() => {
    setReportHistoryTab('saved');
  }, [activeProjectId]);

  const savedReportRecords = useMemo(
    () => (mode === 'report' ? records.filter((r) => isSavedReportStatus(r.status)) : []),
    [mode, records]
  );

  const submittedReportRecords = useMemo(
    () =>
      mode === 'report'
        ? records.filter((r) => normalizeReportStatus(r.status) === 'submitted')
        : [],
    [mode, records]
  );

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
  }, []);

  useEffect(() => {
    const loadCompanies = async () => {
      setLoadingCompanies(true);
      setCompaniesError(null);
      try {
        const result = await getCompanyList(1, 500, 'name-asc', '');
        const items = result.items ?? [];
        const normalized = items
          .map((item) => normalizeCompany(item as Record<string, unknown>))
          .filter((item): item is CompanyRow => item !== null);
        setCompanies(normalized);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load companies.';
        setCompaniesError(message);
        setCompanies([]);
      } finally {
        setLoadingCompanies(false);
      }
    };

    loadCompanies();
  }, []);

  const fetchProjectsForCompany = useCallback(async (companyId: number) => {
    setLoadingProjects((prev) => ({ ...prev, [companyId]: true }));
    try {
      const projectData = await getProjectList(companyId);
      const list = Array.isArray(projectData) ? projectData : [];
      const normalized = list
        .map((item) => normalizeProject(item as Record<string, unknown>))
        .filter((item): item is ProjectRow => item !== null);
      setProjectsByCompany((prev) => ({ ...prev, [companyId]: normalized }));
    } catch {
      setProjectsByCompany((prev) => ({ ...prev, [companyId]: [] }));
    } finally {
      setLoadingProjects((prev) => ({ ...prev, [companyId]: false }));
    }
  }, []);

  const toggleVisibility = (companyId: number) => {
    if (visibleSection === companyId) {
      setVisibleSection(null);
      setSelectedCard(null);
      setActiveProject(null);
      return;
    }

    setVisibleSection(companyId);
    setSelectedCard(companyId);
    setActiveProject(null);
    fetchProjectsForCompany(companyId);
  };

  const openProject = (project: ProjectRow) => {
    setActiveProject(project);
  };

  const backToProjects = () => {
    setActiveProject(null);
    setDetailRecord(null);
    setShowDetail(false);
  };

  const openRecordDetail = async (record: ProjectOversightRecord) => {
    if (!activeProject || !selectedCompany) {
      return;
    }

    setDetailRecord(record);
    setShowDetail(true);

    if (mode === 'chat') {
      if (!record.clientUserId) {
        dashboardToast.error('Missing client reference for this conversation.');
        setShowDetail(false);
        setDetailRecord(null);
        return;
      }
      setDetailGroups([]);
      setDetailLoading(false);
      return;
    }

    setDetailLoading(true);
    setDetailGroups([]);

    const contextFields = [
      { label: 'Company', value: selectedCompany.companyName },
      { label: 'Project', value: activeProject.projectName },
    ];

    try {
      if (mode === 'report') {
        setDetailGroups([
          {
            title: 'Report',
            fields: [
              { label: 'Report ID', value: record.reportItemId || '—' },
              { label: 'Remarks', value: record.subtitle || '—', fullWidth: true },
              { label: 'Generated', value: formatReportTimestamp(record.generatedAt) },
              { label: 'Submitted', value: formatReportTimestamp(record.submittedAt) },
              { label: 'Attachment', value: record.fileName || '—' },
              ...contextFields,
            ],
          },
        ]);
        return;
      }

      if (!record.clientUserId) {
        throw new Error('Missing client reference for this record.');
      }

      if (mode === 'assessment') {
        const detail = await fetchProjectOversightAssessmentDetail(
          activeProject.id,
          record.clientUserId
        );

        setDetailGroups([
          {
            title: 'Assessment',
            fields: [
              { label: 'Client', value: detail.clientName },
              { label: 'Assigned SME', value: detail.assignedSmeName || '—' },
              {
                label: 'Status',
                value: normalizeAssessmentProgressStatus(detail.status),
              },
              { label: 'Last activity', value: formatDate(detail.lastActivityUtc) },
              ...contextFields,
            ],
          },
          {
            title: 'Responses',
            content: renderAssessmentResponses(detail.lines),
          },
        ]);
        return;
      }

      dashboardToast.error('Unsupported oversight mode.');
      setShowDetail(false);
      setDetailRecord(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load record detail.';
      dashboardToast.error(message);
      setShowDetail(false);
      setDetailRecord(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeRecordDetail = () => {
    setShowDetail(false);
    setDetailRecord(null);
    setDetailGroups([]);
    setDetailLoading(false);
  };

  const downloadOversightReport = async (record: ProjectOversightRecord) => {
    if (!record.reportItemId || !activeProject?.id) {
      return;
    }

    try {
      await downloadReport(
        activeProject.id,
        record.reportItemId,
        record.fileName || 'report.pdf'
      );
    } catch {
      dashboardToast.error('Unable to download this report.');
    }
  };

  const handleDownloadReport = async () => {
    if (!detailRecord) return;
    await downloadOversightReport(detailRecord);
  };

  const reportHasFile = (record: ProjectOversightRecord) =>
    Boolean(record.fileName?.trim() || record.reportItemId?.trim());

  const reportIsPdf = (record: ProjectOversightRecord) =>
    /\.pdf$/i.test(record.fileName || '');

  const selectedCompany = companies.find((company) => company.id === visibleSection);
  const searchNeedle = searchTerm.trim().toLowerCase();

  const companyDisplayName = (company: CompanyRow) => company.companyName;
  const projectDisplayName = (project: ProjectRow) => project.projectName;

  const companyMatchesSearch = (company: CompanyRow) =>
    !searchNeedle || companyDisplayName(company).toLowerCase().includes(searchNeedle);

  const projectMatchesSearch = (project: ProjectRow) =>
    !searchNeedle || projectDisplayName(project).toLowerCase().includes(searchNeedle);

  const displayedCompanies = useMemo(
    () =>
      sortTableRows(
        companies.filter((company) => {
          if (company.id === visibleSection || companyMatchesSearch(company)) return true;
          return (projectsByCompany[company.id] || []).some(projectMatchesSearch);
        }),
        sortKey,
        {
          name: companyDisplayName,
          date: (company) => company.id,
        }
      ),
    [companies, visibleSection, projectsByCompany, searchNeedle, sortKey]
  );

  const displayedProjects = useMemo(() => {
    if (!visibleSection) return [];
    return sortTableRows(
      (projectsByCompany[visibleSection] || []).filter(
        (project) =>
          (selectedCompany && companyMatchesSearch(selectedCompany)) ||
          projectMatchesSearch(project)
      ),
      sortKey,
      {
        name: projectDisplayName,
        date: (project) => {
          const timestamp = Date.parse(project.projectCreationDate || '');
          return Number.isNaN(timestamp) ? project.id : timestamp;
        },
      }
    );
  }, [visibleSection, projectsByCompany, selectedCompany, searchNeedle, sortKey]);

  const displayedRecords = useMemo(() => {
    const filtered = searchNeedle
      ? records.filter((record) => {
          const haystack = [
            record.reportItemId,
            record.title,
            record.subtitle,
            record.fileName,
            record.status,
            record.clientName,
            record.smeName,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(searchNeedle);
        })
      : records;

    return sortTableRows(filtered, sortKey, {
      name: (record) => record.reportItemId || record.title,
      date: (record) => {
        const raw = record.submittedAt || record.generatedAt || record.lastActivity;
        const ts = Date.parse(raw || '');
        return Number.isNaN(ts) ? 0 : ts;
      },
    });
  }, [records, searchNeedle, sortKey]);

  const reportTabSource = useMemo(
    () => (reportHistoryTab === 'saved' ? savedReportRecords : submittedReportRecords),
    [reportHistoryTab, savedReportRecords, submittedReportRecords]
  );

  const displayedReportRecords = useMemo(() => {
    const filtered = searchNeedle
      ? reportTabSource.filter((record) => {
          const haystack = [
            record.reportItemId,
            record.title,
            record.subtitle,
            record.fileName,
            record.status,
            reportStatusLabel(record.status),
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(searchNeedle);
        })
      : reportTabSource;

    return sortTableRows(filtered, sortKey, {
      name: (record) => record.reportItemId || record.title,
      date: (record) => {
        const raw = record.submittedAt || record.generatedAt || record.lastActivity;
        const ts = Date.parse(raw || '');
        return Number.isNaN(ts) ? 0 : ts;
      },
    });
  }, [reportTabSource, searchNeedle, sortKey]);

  const renderRecordsPanel = () => {
    if (!activeProject || !selectedCompany) {
      return null;
    }

    if (loadingRecords) {
      return (
        <ContentLoader
          compact
          label={`Loading ${config.recordNounPlural} for ${activeProject.projectName}…`}
        />
      );
    }

    if (recordsError) {
      return <Alert variant="error">{recordsError}</Alert>;
    }

    if (records.length === 0) {
      return (
        <div className="pm-empty-state">
          <p>
            <strong>{config.emptyRecordsTitle}</strong>
          </p>
          <p className="pm-oversight-empty-hint">{config.emptyRecordsHint}</p>
        </div>
      );
    }

    if (mode !== 'report' && displayedRecords.length === 0) {
      return (
        <div className="pm-empty-state pm-empty-state--compact">
          <p>
            {searchNeedle
              ? `No ${config.recordNounPlural} match "${searchTerm.trim()}".`
              : `No ${config.recordNounPlural} to show.`}
          </p>
        </div>
      );
    }

    if (mode === 'report') {
      const isSubmittedTab = reportHistoryTab === 'submitted';
      const reportRows = displayedReportRecords;
      const emptyTabMessage =
        reportHistoryTab === 'saved'
          ? 'No saved drafts for this project yet.'
          : 'No submitted reports for this project yet.';

      if (reportTabSource.length === 0) {
        return (
          <>
            <Tabs
              variant="segmented"
              className="pm-oversight-report-tabs"
              value={reportHistoryTab}
              onChange={(id) => setReportHistoryTab(id as 'saved' | 'submitted')}
              tabs={[
                { id: 'saved', label: `Saved (${savedReportRecords.length})`, content: null },
                {
                  id: 'submitted',
                  label: `Submitted (${submittedReportRecords.length})`,
                  content: null,
                },
              ]}
            />
            <div className="pm-empty-state pm-empty-state--compact pm-oversight-report-empty">
              <p>{emptyTabMessage}</p>
            </div>
          </>
        );
      }

      if (reportRows.length === 0) {
        return (
          <>
            <Tabs
              variant="segmented"
              className="pm-oversight-report-tabs"
              value={reportHistoryTab}
              onChange={(id) => setReportHistoryTab(id as 'saved' | 'submitted')}
              tabs={[
                { id: 'saved', label: `Saved (${savedReportRecords.length})`, content: null },
                {
                  id: 'submitted',
                  label: `Submitted (${submittedReportRecords.length})`,
                  content: null,
                },
              ]}
            />
            <div className="pm-empty-state pm-empty-state--compact pm-oversight-report-empty">
              <p>
                {searchNeedle
                  ? `No reports match "${searchTerm.trim()}".`
                  : emptyTabMessage}
              </p>
            </div>
          </>
        );
      }

      return (
        <>
          <Tabs
            variant="segmented"
            className="pm-oversight-report-tabs"
            value={reportHistoryTab}
            onChange={(id) => setReportHistoryTab(id as 'saved' | 'submitted')}
            tabs={[
              { id: 'saved', label: `Saved (${savedReportRecords.length})`, content: null },
              {
                id: 'submitted',
                label: `Submitted (${submittedReportRecords.length})`,
                content: null,
              },
            ]}
          />
          <div
            className={cn(
              'app-table-wrap app-table-wrap--refined pm-oversight-report-table dash-table-mobile-skip',
              isSubmittedTab && 'pm-oversight-report-table--submitted-tab'
            )}
          >
            <table className="table table-striped">
              <thead>
                <tr>
                  <th scope="col" className="pm-oversight-report-table__index-col">
                    #
                  </th>
                  <th scope="col" className="pm-oversight-report-table__id-col">
                    Report ID
                  </th>
                  <th scope="col" className="pm-oversight-report-table__status-col">
                    Status
                  </th>
                  <th scope="col" className="pm-oversight-report-table__remarks-col">
                    Remarks
                  </th>
                  <th scope="col" className="pm-oversight-report-table__date-col">
                    Created
                  </th>
                  {isSubmittedTab ? (
                    <th scope="col" className="pm-oversight-report-table__submitted-col">
                      Submitted
                    </th>
                  ) : null}
                  <th scope="col" className="pm-oversight-report-table__file-col">
                    File
                  </th>
                  <th scope="col" className="pm-oversight-report-table__actions-col">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {reportRows.map((record, index) => (
                <tr
                  key={record.id}
                  className="rdm-row-clickable"
                  onClick={() => openRecordDetail(record)}
                >
                  <td className="pm-oversight-report-table__index-col">
                    <span className="app-td__index">{index + 1}</span>
                  </td>
                  <td
                    className="pm-oversight-report-table__id-col"
                    title={record.reportItemId || record.title}
                  >
                    <span className="app-td__title">
                      {record.reportItemId?.trim() || record.title}
                    </span>
                  </td>
                  <td className="pm-oversight-report-table__status-col">
                    <span className={reportStatusClass(record.status)}>
                      {reportStatusLabel(record.status)}
                    </span>
                  </td>
                  <td className="pm-oversight-report-table__remarks-col">
                    {record.subtitle?.trim() ? (
                      <span className="app-td__desc pm-oversight-report-table__remarks">
                        {record.subtitle}
                      </span>
                    ) : (
                      <span className="app-td__empty">—</span>
                    )}
                  </td>
                  <td className="pm-oversight-report-table__date-col">
                    <span className="app-td__meta pm-oversight-report-table__date">
                      {formatReportTimestamp(record.generatedAt)}
                    </span>
                  </td>
                  {isSubmittedTab ? (
                    <td className="pm-oversight-report-table__submitted-col">
                      {record.submittedAt ? (
                        <span className="app-td__meta pm-oversight-report-table__date">
                          {formatReportTimestamp(record.submittedAt)}
                        </span>
                      ) : (
                        <span className="app-td__empty">—</span>
                      )}
                    </td>
                  ) : null}
                  <td className="pm-oversight-report-table__file-col">
                    {record.fileName ? (
                      <span
                        className="app-td__meta pm-oversight-report-table__file"
                        title={record.fileName}
                      >
                        {record.fileName}
                      </span>
                    ) : (
                      <span className="app-td__desc">No file</span>
                    )}
                  </td>
                  <td
                    className="pm-oversight-report-table__actions pm-oversight-report-table__actions-col"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {reportHasFile(record) ? (
                      <div className="btn-list-actions pm-oversight-report-table__action-group pm-oversight-report-table__action-group--compact">
                        {reportIsPdf(record) ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="primary"
                            className="pm-oversight-report-table__action-btn"
                            onClick={() => setPreviewReport(record)}
                          >
                            Preview
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          className="pm-oversight-report-table__action-btn"
                          onClick={() => void downloadOversightReport(record)}
                        >
                          Download
                        </Button>
                      </div>
                    ) : (
                      <span className="app-td__desc">No attachment</span>
                    )}
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      );
    }

    return (
      <div className="pm-project-list">
        {displayedRecords.map((record) => (
          <article
            key={record.id}
            className="pm-project-card rdm-card-clickable"
            onClick={() => openRecordDetail(record)}
          >
            <div className="pm-project-card__header">
              <h3 className="pm-project-card__title">{record.title}</h3>
            </div>
            <dl className="pm-project-card__meta">
              {record.clientName ? (
                <div className="pm-project-card__meta-row">
                  <dt>Client</dt>
                  <dd>{record.clientName}</dd>
                </div>
              ) : null}
              {record.smeName ? (
                <div className="pm-project-card__meta-row">
                  <dt>SME</dt>
                  <dd>{record.smeName}</dd>
                </div>
              ) : null}
              {record.status ? (
                <div className="pm-project-card__meta-row">
                  <dt>Status</dt>
                  <dd>
                    <span className={statusClass(record.status)}>
                      {normalizeAssessmentProgressStatus(record.status)}
                    </span>
                  </dd>
                </div>
              ) : null}
              <div className="pm-project-card__meta-row">
                <dt>Last activity</dt>
                <dd>{formatDate(record.lastActivity || record.generatedAt)}</dd>
              </div>
              {record.subtitle ? (
                <div className="pm-project-card__meta-row">
                  <dt>Summary</dt>
                  <dd>{record.subtitle}</dd>
                </div>
              ) : null}
            </dl>
          </article>
        ))}
      </div>
    );
  };

  const renderProjectsPanel = () => {
    if (!visibleSection) {
      return (
        <div className="pm-placeholder">
          <p>Select a company to browse its projects.</p>
        </div>
      );
    }

    if (activeProject && selectedCompany) {
      return (
        <div className="pm-oversight-records">
          <div className="pm-oversight-records__head">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="pm-oversight-back"
              onClick={backToProjects}
            >
              <ArrowLeft size={16} aria-hidden />
              Back to projects
            </Button>
            <p className="pm-oversight-records__context">
              {selectedCompany.companyName} · {activeProject.projectName}
            </p>
          </div>
          {renderRecordsPanel()}
        </div>
      );
    }

    if (loadingProjects[visibleSection]) {
      return (
        <ContentLoader
          compact
          label={`Loading projects for ${selectedCompany?.companyName || 'company'}…`}
        />
      );
    }

    if (displayedProjects.length === 0) {
      return (
        <div className="pm-empty-state pm-empty-state--compact">
          <p>
            {searchNeedle
              ? `No matching projects for ${selectedCompany?.companyName || 'this company'}.`
              : `No projects yet for ${selectedCompany?.companyName || 'this company'}.`}
          </p>
        </div>
      );
    }

    return (
      <div className="pm-project-list">
        {displayedProjects.map((project) => (
          <article
            key={project.id}
            className="pm-project-card rdm-card-clickable"
            onClick={() => openProject(project)}
          >
            <div className="pm-project-card__header">
              <h3 className="pm-project-card__title">{project.projectName}</h3>
            </div>
            <dl className="pm-project-card__meta">
              <div className="pm-project-card__meta-row">
                <dt>Reference</dt>
                <dd>{project.projectNumber || '—'}</dd>
              </div>
              <div className="pm-project-card__meta-row">
                <dt>Department</dt>
                <dd>{project.departmentName || '—'}</dd>
              </div>
              <div className="pm-project-card__meta-row">
                <dt>Created</dt>
                <dd>
                  {project.projectCreationDate
                    ? new Date(project.projectCreationDate).toLocaleDateString()
                    : '—'}
                </dd>
              </div>
            </dl>
            <p className="pm-oversight-project-hint">{config.selectProjectHint}</p>
          </article>
        ))}
      </div>
    );
  };

  if (loadingCompanies) {
    return (
      <DashboardShell
        isMenuCollapsed={isMenuCollapsed}
        sidebar={
          <Menu
            onSelect={handleMenuSelection}
            selectedItem={selectedItem}
            isCollapsed={isMenuCollapsed}
            onToggleCollapse={toggleMenuCollapse}
          />
        }
      >
        <SaListPageShell topBarTitle={config.topBarTitle}>
          <ContentLoader label="Loading companies…" />
        </SaListPageShell>
      </DashboardShell>
    );
  }

  const rightPanelTitle = activeProject
    ? `${config.recordsPanelLabel} — ${activeProject.projectName}`
    : selectedCompany
      ? `Projects — ${selectedCompany.companyName}`
      : 'Projects';

  return (
    <>
      <DashboardShell
        isMenuCollapsed={isMenuCollapsed}
        sidebar={
          <Menu
            onSelect={handleMenuSelection}
            selectedItem={selectedItem}
            isCollapsed={isMenuCollapsed}
            onToggleCollapse={toggleMenuCollapse}
          />
        }
      >
        <SaListPageShell topBarTitle={config.topBarTitle}>
          <div className="dash-frame-parent1 pm-project-management">
            <div className="dash-list-toolbar">
              <div className="dash-list-toolbar__intro">
                <h5 className="dash-list-toolbar__title">{config.toolbarTitle}</h5>
                <p className="dash-list-toolbar__desc">{config.toolbarDesc}</p>
              </div>
              <div className="dash-list-toolbar__actions">
                <div className="dash-search-field dash-table-search">
                  <Search className="dash-search-field__icon" aria-hidden />
                  <input
                    type="text"
                    placeholder="Search company or project…"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="dash-search-field__input"
                  />
                </div>
                <TableSortSelect variant="chip" value={sortKey} onChange={setSortKey} />
              </div>
            </div>

            {companiesError ? <Alert variant="error">{companiesError}</Alert> : null}

            <div className="pm-layout">
              <aside className="pm-panel pm-panel--companies">
                <h2 className="pm-panel__title">Companies</h2>
                <div className="pm-list">
                  {displayedCompanies.length === 0 ? (
                    <div className="pm-empty-state pm-empty-state--compact">
                      <p>No companies found.</p>
                    </div>
                  ) : (
                    displayedCompanies.map((company) => {
                      const isSelected = selectedCard === company.id;
                      const isOpen = visibleSection === company.id;

                      return (
                        <div
                          key={company.id}
                          className={`pm-list-card ${isSelected ? 'pm-list-card--selected' : ''}`}
                          onClick={() => toggleVisibility(company.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              toggleVisibility(company.id);
                            }
                          }}
                        >
                          <div className="pm-list-card__body">
                            <div className="pm-list-card__title">{company.companyName}</div>
                            {company.industry ? (
                              <div className="pm-list-card__subtitle">{company.industry}</div>
                            ) : null}
                            {company.companyInfo ? (
                              <div className="pm-list-card__subtitle">{company.companyInfo}</div>
                            ) : null}
                          </div>
                          {isOpen ? (
                            <ChevronDown className="pm-chevron-icon" aria-hidden />
                          ) : (
                            <ChevronRight className="pm-chevron-icon" aria-hidden />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </aside>

              <section className="pm-panel pm-panel--projects">
                <h2 className="pm-panel__title">{rightPanelTitle}</h2>
                {renderProjectsPanel()}
              </section>
            </div>
          </div>
        </SaListPageShell>
      </DashboardShell>

      <RecordDetailModal
        show={showDetail}
        onHide={closeRecordDetail}
        title={detailRecord?.title || config.recordNoun}
        subtitle={
          activeProject && selectedCompany
            ? `${selectedCompany.companyName} · ${activeProject.projectName}${
                mode === 'chat' && detailRecord?.smeName
                  ? ` · SME: ${detailRecord.smeName}`
                  : ''
              }`
            : undefined
        }
        groups={mode === 'chat' ? [] : detailGroups}
        size={mode === 'chat' || mode === 'assessment' ? 'xl' : 'lg'}
        dialogClassName={
          mode === 'chat'
            ? 'sa-oversight-chat-modal'
            : mode === 'assessment'
              ? 'sa-oversight-assessment-modal'
              : undefined
        }
        bodyClassName={
          mode === 'chat'
            ? 'rdm-body--chat-embed'
            : mode === 'assessment'
              ? 'rdm-body--assessment-oversight'
              : undefined
        }
        actions={
          mode === 'report' && detailRecord && reportHasFile(detailRecord)
            ? [
                ...(reportIsPdf(detailRecord)
                  ? [
                      {
                        type: 'preview' as const,
                        onClick: () => {
                          const record = detailRecord;
                          closeRecordDetail();
                          if (record) setPreviewReport(record);
                        },
                      },
                    ]
                  : []),
                { type: 'download' as const, onClick: handleDownloadReport, label: 'Download' },
              ]
            : undefined
        }
      >
        {mode === 'chat' && detailRecord?.clientUserId && activeProject ? (
          <div className="sme-tracking-page sa-oversight-chat-modal-body">
            <div className="sme-assessment-container sme-tracking-chat-panel su-chat-panel">
              <div className="sme-workspace sa-oversight-chat-workspace">
                <div className="sme-chat-panel client-chat-shell">
                  <Chat
                    key={`sa-oversight-chat-${activeProject.id}-${detailRecord.clientUserId}`}
                    project={{ projectId: activeProject.id, ...activeProject }}
                    clientUserId={detailRecord.clientUserId}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
        ) : detailLoading ? (
          <ContentLoader compact label="Loading details…" />
        ) : null}
      </RecordDetailModal>

      {mode === 'report' ? (
        <PdfReportPreviewModal
          show={Boolean(previewReport)}
          onHide={() => setPreviewReport(null)}
          title="Capability Assessment Report"
          projectId={activeProject?.id}
          reportItemId={previewReport?.reportItemId}
          fileName={previewReport?.fileName || 'report.pdf'}
          onDownload={
            previewReport
              ? () => void downloadOversightReport(previewReport)
              : undefined
          }
        />
      ) : null}
    </>
  );
}

export default ProjectOversightLayout;
